import React, { useCallback, useEffect, useState } from 'react';
import { Typography } from '@mui/material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';

import biService from '../../../service/bi.service';
import { BiChartType, BiReportType } from '../../../config/bi_report';
import DrillSelectionPath from './DrillSelectionPath';
import {
  BI_CHART_TOOLTIP_OPTIONS,
  createBiChartLabelTooltipPlugin,
  truncateBiChartLabel,
} from '../../../utils/biChartLabel';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend);

const DEFAULT_BORDER_COLORS = [
  'rgb(54, 162, 235)',
  'rgb(255, 99, 132)',
  'rgb(255, 159, 64)',
  'rgb(255, 205, 86)',
  'rgb(75, 192, 192)',
  'rgb(153, 102, 255)',
  'rgb(201, 203, 207)',
];
const DEFAULT_BACKGROUND_COLORS = DEFAULT_BORDER_COLORS.map((color) =>
  color.replace('rgb(', 'rgba(').replace(')', ', 0.5)')
);

const formatChartValue = (value) => {
  if (typeof value !== 'number') return String(value);
  return new Intl.NumberFormat('hu-HU', { maximumFractionDigits: 20 }).format(value);
};

const chartValueLabelsPlugin = {
  id: 'biChartValueLabels',
  afterDatasetsDraw: (chart) => {
    const { ctx, chartArea } = chart;
    const fontSize = ChartJS.defaults.font.size;

    ctx.save();
    ctx.font = `${fontSize}px ${ChartJS.defaults.font.family}`;
    ctx.fillStyle = chart.options.color ?? ChartJS.defaults.color;

    chart.data.datasets.forEach((dataset, datasetIndex) => {
      if (!dataset.showDataLabels) return;

      const meta = chart.getDatasetMeta(datasetIndex);
      if (meta.hidden) return;

      const isHorizontalBar = meta.type === 'bar' && chart.options.indexAxis === 'y';

      meta.data.forEach((element, dataIndex) => {
        if (element.hidden || element.skip) return;

        const parsedValue = meta.controller.getParsed(dataIndex);
        if (!parsedValue) return;
        const value = isHorizontalBar ? parsedValue.x : parsedValue.y;
        if (value === null || value === undefined || !Number.isFinite(Number(value))) return;

        const label = formatChartValue(value);
        const textWidth = ctx.measureText(label).width;
        let x = element.x;
        let y = element.y;

        if (isHorizontalBar) {
          const isPositive = Number(value) >= 0;
          ctx.textAlign = isPositive ? 'left' : 'right';
          ctx.textBaseline = 'middle';
          x += isPositive ? 6 : -6;
          x = isPositive
            ? Math.min(x, chartArea.right - textWidth)
            : Math.max(x, chartArea.left + textWidth);
        } else {
          const isPositive = meta.type === 'line' || Number(value) >= 0;
          ctx.textAlign = 'center';
          ctx.textBaseline = isPositive ? 'bottom' : 'top';
          x = Math.min(Math.max(x, chartArea.left + textWidth / 2), chartArea.right - textWidth / 2);
          y += isPositive ? -6 : 6;
          y = isPositive
            ? Math.max(y, chartArea.top + fontSize)
            : Math.min(y, chartArea.bottom - fontSize);
        }

        ctx.fillText(label, x, y);
      });
    });

    ctx.restore();
  },
};

const BarReport = ({ report, linkedReportIdState, activeLevelState, filterValsState, reportStatesState, height }) => {
  const [chartData, setChartData] = useState({});
  const [chartHeight, setChartHeight] = useState(650);

  const { activeLevel, setActiveLevel } = activeLevelState;
  const { filterVals, setFilterVals } = filterValsState;
  const { linkedReportId, setLinkedReportId } = linkedReportIdState;
  const { reportStates, setReportStates } = reportStatesState;

  const getChartData = useCallback(() => {
    biService.getReportData(report.id, activeLevel, filterVals).then((data) => setChartData(data));
  }, [report.id, activeLevel, filterVals]);

  useEffect(() => {
    const prevReport = reportStates[reportStates.length - 1];
    if (Object.keys(report).length > 0 && prevReport?.reportId !== report.id) {
      getChartData();
    }
  }, [report, getChartData, activeLevel, reportStates]);

  useEffect(() => {
    if (height) setChartHeight(height);
  }, [height]);

  const hasDrillSelections = (filterVals?.filter ?? []).some((item) => !item?.isDefault);

  const handleResetDrill = useCallback(() => {
    setFilterVals((prev) => {
      const defaultFilters = (prev?.filter ?? []).filter((item) => item?.isDefault);
      return { ...prev, filter: defaultFilters };
    });
    setActiveLevel(null);
  }, [setFilterVals, setActiveLevel]);

  const handleSelectDrillLevel = useCallback(
    (targetIndex) => {
      setFilterVals((prev) => {
        const defaultFilters = (prev?.filter ?? []).filter((item) => item?.isDefault);
        const drillFilters = (prev?.filter ?? []).filter((item) => !item?.isDefault);
        const nextDrillFilters = drillFilters.slice(0, targetIndex + 1);
        return { ...prev, filter: [...defaultFilters, ...nextDrillFilters] };
      });
      setActiveLevel((prev) => (prev === null ? null : targetIndex));
    },
    [setFilterVals, setActiveLevel]
  );

  const chartFields = report.biField?.filter((field) => !field.isGroup && !field.isFilter) ?? [];
  const groupLabelMaxLength = report.biField?.find((field) => field.isGroup)?.groupLabelMaxLength;
  const categoryAxis = report.reportType === BiReportType.HORIZONTAL ? 'y' : 'x';
  const valueAxis = categoryAxis === 'x' ? 'y' : 'x';
  const chartLabelTooltipPlugin = createBiChartLabelTooltipPlugin({
    maxLength: groupLabelMaxLength,
    categoryAxis,
  });
  const data = {
    labels: chartData.label,
    datasets: chartData?.chart
      ? chartData.chart.map((c, index) => {
          const chartType = c.chartType ?? chartFields[index]?.chartType;
          const color = chartFields[index]?.color;
          return {
            ...c,
            ...(report.reportType === BiReportType.COMBO && {
              type: chartType === BiChartType.LINE ? 'line' : 'bar',
            }),
            showDataLabels: chartFields[index]?.showDataLabels === true,
            backgroundColor: color ?? DEFAULT_BACKGROUND_COLORS[index % DEFAULT_BACKGROUND_COLORS.length],
            borderColor: color ?? DEFAULT_BORDER_COLORS[index % DEFAULT_BORDER_COLORS.length],
            borderWidth: 1,
            hoverOffset: 4,
          };
        })
      : [],
  };
  const ChartComponent = report.reportType === BiReportType.LINE ? Line : Bar;

  const options = {
    ...(report.reportType === BiReportType.HORIZONTAL && { indexAxis: 'y' }),
    maintainAspectRatio: false,
    aspectRatio: 1,
    scales: {
      [valueAxis]: {
        grace: '10%',
      },
      [categoryAxis]: {
        ticks: {
          callback(value) {
            return truncateBiChartLabel(this.getLabelForValue(value), groupLabelMaxLength);
          },
        },
      },
    },
    plugins: {
      tooltip: BI_CHART_TOOLTIP_OPTIONS,
      colors: {
        enabled: false,
      },
      title: {
        display: false,
        text: report.name,
      },
    },
    onClick: (event, elements) => {
      //@TODO majd egy központi fg jó lenne a click eseményekre
      if (elements.length > 0) {
        const { biDrillingLink, biDrilling, biField, id } = report;

        const label = data.labels[elements[0].index];
        const maxLevel = biDrilling.length > 0 ? Math.max(...biDrilling.map((drill) => drill.level)) : null;
        let actLevel = activeLevel !== null ? activeLevel + 1 : 0;

        let filterField;

        if (actLevel === 0 || actLevel === null) {
          filterField = biField.find((f) => f.isGroup)?.field;
        } else {
          filterField = biDrilling?.find((f) => {
            return f.level === activeLevel;
          })?.field;
        }

        const newFilter = { ...filterVals };

        if (maxLevel + 1 === actLevel || (biDrillingLink && maxLevel === null)) {
          if (biDrillingLink) {
            setReportStates((prev) => {
              prev.push({
                reportId: id,
                linkedReportId: linkedReportId,
                filter: { filter: newFilter.filter.map((data) => data) },
                activeLevel,
              });
              return prev;
            });
            setLinkedReportId(biDrillingLink.linkedReport);

            newFilter.filter.push({ field: filterField, value: label });

            const updatedFilter = newFilter.filter.map((item) => {
              const newReportField = biDrillingLink.biDrillingLinkField.find(
                (linkField) => linkField.parentField === item.field
              );
              if (newReportField) {
                return { ...item, field: newReportField.childField };
              }
              return item;
            });

            setFilterVals({ filter: updatedFilter });
            setActiveLevel(null);
          } else {
            return;
          }
        } else {
          newFilter.filter.push({ field: filterField, value: label });
          if (!biDrilling.length > 0) {
            return;
          }
          setActiveLevel(actLevel);
          setFilterVals(newFilter);
        }
      }
    },
  };

  return (
    <>
      <div className="flex flex-col items-center mt-2">
        <button
          type="button"
          onClick={handleResetDrill}
          disabled={!hasDrillSelections}
          className={`${hasDrillSelections ? 'cursor-pointer hover:underline' : 'cursor-default'}`}
        >
          <Typography variant="h6" sx={{ color: 'text.primary' }}>
            {report.name}
          </Typography>
        </button>
        <DrillSelectionPath filterVals={filterVals} onSelectLevel={handleSelectDrillLevel} />
      </div>
      <div className="mx-2 flex justify-center" style={{ height: `${chartHeight}px` }}>
        <ChartComponent
          data={data}
          options={options}
          plugins={[chartLabelTooltipPlugin, chartValueLabelsPlugin]}
        />
      </div>
    </>
  );
};

export default BarReport;
